/**
 * What the phones read off their own TikTok profiles (plan Stage 3, owner 2026-10-12: "3 виглядає наче
 * добре, роби, далі скоригуємо").
 *
 * The service drives the reading (`profile_pass.py`); this shows it: the schedule and its switch (admin),
 * each account's last reading in one line — with what went wrong in words, never as a bare code — and,
 * opened, every post read with its numbers and what it was matched to. A doubtful match is shown as such:
 * a person settles it, nothing here guesses.
 */

import { Bookmark, Eye, Heart, MessageCircle, Share2 } from 'lucide-react'
import { useState } from 'react'

import type { MarketingAccount, PostStats, ProfileBrief, ProfileSnapshot } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { Card, Status } from '../../ui/components'

function errorText(e: unknown): string {
  const d = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
  return d?.hint ?? (e instanceof Error ? e.message : String(e))
}

function when(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''
}

const n = (v?: number | null) => (v == null ? '—' : v.toLocaleString())

const ICONS = { views: Eye, likes: Heart, comments: MessageCircle, shares: Share2, saves: Bookmark } as const

/** 👁 356 · ♥ 4 · … as icons with their names spoken, numbers aligned. */
export function PostStatsLine({ stats }: { stats: PostStats }) {
  const names = useT().profile.statNames
  return (
    <span className="post-stats tiny">
      {(Object.keys(ICONS) as (keyof typeof ICONS)[]).map((k) => {
        const Icon = ICONS[k]
        return (
          <span key={k} className="post-stat" title={names[k]}>
            <Icon size={12} className="ico" aria-hidden /> <span className="sr-only">{names[k]} </span>{n(stats[k])}
          </span>
        )
      })}
    </span>
  )
}

/** The schedule: on or off, which account is being read, the switch for an admin. */
export function ProfilePassCard({ accounts }: { accounts: MarketingAccount[] }) {
  const t = useT().profile
  const { api, auth, authEpoch } = useApp()
  const state = usePoll(() => api.profilePass(), 15000, [api, authEpoch])
  const [problem, setProblem] = useState<string | null>(null)
  const s = state.data
  const running = s?.running ? accounts.find((a) => a.account_id === s.running)?.label ?? s.running : null
  const flip = async () => {
    if (!s) return
    setProblem(null)
    try {
      await api.setProfilePass(!s.auto)
      state.refresh()
    } catch (e) {
      setProblem(errorText(e))
    }
  }
  return (
    <Card title={t.title} sub={t.sub}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <span className={`pass-state ${s?.auto ? 'on' : 'off'}`}>{s?.auto ? t.autoOn : t.autoOff}</span>
        {running && <span className="tiny">{t.readingNow(running)}</span>}
        {auth.isAdmin && s && (
          <button className={s.auto ? 'quiet' : 'go'} onClick={() => void flip()}>{s.auto ? t.switchOff : t.switchOn}</button>
        )}
      </div>
      <p className="tiny dim">{t.rules}</p>
      <Status text={problem} bad />
    </Card>
  )
}

/** One account's last reading, in a line; opened — every post read. */
export function ProfileLine({ account: a, onChanged }: { account: MarketingAccount; onChanged: () => void }) {
  const t = useT().profile
  const { api, auth } = useApp()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const p: ProfileBrief | null | undefined = a.profile
  const read = async () => {
    setProblem(null)
    try {
      await api.readProfile(a.account_id)
      setNote(t.started)
      window.setTimeout(onChanged, 60000)
      window.setTimeout(onChanged, 150000)
    } catch (e) {
      setProblem(errorText(e))
    }
  }
  const bad = p && p.outcome !== 'ok'
  return (
    <div className="profile-line">
      <div className="row" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'baseline' }}>
        {!p && <span className="tiny dim">{t.never}</span>}
        {p && p.outcome === 'ok' && (
          <span className="tiny">{t.counts(n(p.followers), n(p.likes), p.videos_read, n(p.views))}</span>
        )}
        {bad && <span className="tiny warn-text">{t.outcome[p.outcome] ?? p.outcome}</span>}
        {p && <span className="tiny dim">· {t.when(when(p.at))}</span>}
        {p && p.doubtful > 0 && <span className="tiny warn-text">· {t.doubtful(p.doubtful)}</span>}
        {p && p.not_found > 0 && <span className="tiny warn-text">· {t.notFound(p.not_found)}</span>}
        {p && p.videos_read > 0 && (
          <button className="quiet tiny" onClick={() => setOpen(!open)} aria-expanded={open}>{t.details}</button>
        )}
        {auth.isAdmin && a.device_id && (
          <button className="quiet tiny" onClick={() => void read()}>{t.readNow}</button>
        )}
      </div>
      {p && p.alerts.length > 0 && <div className="tiny warn-text">{p.alerts.join(' · ')}</div>}
      {note && <div className="tiny dim">{note}</div>}
      {problem && <div className="tiny bad-text">{problem}</div>}
      {open && <ProfileDetails accountId={a.account_id} />}
    </div>
  )
}

function ProfileDetails({ accountId }: { accountId: string }) {
  const t = useT().profile
  const { api } = useApp()
  const got = usePoll(() => api.accountProfile(accountId, 1), 60000, [api, accountId])
  const snap: ProfileSnapshot | undefined = got.data?.snapshots[0]
  if (!snap) return null
  return (
    <ol className="profile-videos">
      {snap.videos.map((v, i) => (
        <li key={i}>
          <div className="profile-video-head">
            <span className="profile-caption">{v.caption || '—'}</span>
            {v.age && <span className="tiny dim">{v.age}</span>}
            {v.match && <span className={`match-chip ${v.match}`}>{t.match[v.match] ?? v.match}</span>}
          </div>
          <PostStatsLine stats={v} />
          {v.ai_label && <span className="tiny dim"> · {t.aiLabel}</span>}
        </li>
      ))}
    </ol>
  )
}
